import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  BatchCharacteristicRequest,
  BatchCharacteristicResponse,
  BatchCharacteristicUpdate,
} from "@/types/batch-characteristics.types";

// Spec 084 — as características de batelada da organização.
export const batchCharacteristicsKey = ["batch-characteristics"] as const;

/** `lineId` restringe às características marcadas naquela linha (formulário da ordem). */
export function useBatchCharacteristics(opts: { lineId?: string | null; enabled?: boolean } = {}) {
  const lineId = opts.lineId ?? null;
  return useQuery<BatchCharacteristicResponse[]>({
    queryKey: [...batchCharacteristicsKey, lineId ?? "all"],
    enabled: opts.enabled ?? true,
    queryFn: () =>
      api.get<BatchCharacteristicResponse[]>(
        lineId
          ? `/api/v1/batch-characteristics?lineId=${encodeURIComponent(lineId)}`
          : "/api/v1/batch-characteristics"
      ),
  });
}

/**
 * Mudar uma característica muda o que TODA batelada mostra (rótulo, obrigatoriedade, linhas de
 * marcação) e a pendência de marcação das ordens. As chaves são invalidadas por prefixo porque o
 * cliente não sabe quais bateladas e ordens a característica alcança.
 */
function invalidateEverything(qc: ReturnType<typeof useQueryClient>, withReport: boolean) {
  void qc.invalidateQueries({ queryKey: batchCharacteristicsKey });
  void qc.invalidateQueries({ queryKey: ["batch"] });
  void qc.invalidateQueries({ queryKey: ["batches"] });
  void qc.invalidateQueries({ queryKey: ["production-orders"] });
  if (withReport) void qc.invalidateQueries({ queryKey: ["production-report"] });
}

export function useSaveBatchCharacteristic() {
  const qc = useQueryClient();
  // A tela trata o erro com mensagem própria; sem o opt-out o MutationCache global soma o dele.
  return useMutation({
    meta: { skipErrorToast: true },
    mutationFn: (
      args:
        | { id: null; payload: BatchCharacteristicRequest }
        | { id: string; payload: BatchCharacteristicUpdate }
    ) =>
      args.id
        ? api.patch<BatchCharacteristicResponse>(
            `/api/v1/batch-characteristics/${args.id}`,
            args.payload
          )
        : api.post<BatchCharacteristicResponse>("/api/v1/batch-characteristics", args.payload),
    onSuccess: () => invalidateEverything(qc, false),
  });
}

export function useDeleteBatchCharacteristic() {
  const qc = useQueryClient();
  return useMutation({
    meta: { skipErrorToast: true },
    mutationFn: (id: string) => api.delete<void>(`/api/v1/batch-characteristics/${id}`, {}),
    onSuccess: () => invalidateEverything(qc, true),
  });
}
